/**
 * O contrato do credenciamento no portal público — tipos, rótulos, tons e
 * formatadores das telas por onde o transportador se inscreve e acompanha.
 *
 * OS NOMES DOS CAMPOS SÃO OS DAS COLUNAS DO BANCO, em minúsculas com sublinhado,
 * pela mesma razão do contrato das listagens de operação
 * (`modulos/operacao/comum/contrato.ts`): é assim que a API vai responder, e
 * reescrever para camelCase criaria uma tradução a mais para manter em dia sem
 * ganhar nada. A fonte da verdade é `db/migrations/0002_dominio_base.sql` —
 * `inscricoes_credenciamento`, `inscricao_rascunhos`, `inscricao_pendencias`,
 * `inscricao_transicoes` e o tipo enumerado `status_inscricao`.
 *
 * Sem React aqui. Componente e gancho ficam nas telas e em `dados.ts`.
 */

import {
  AlertCircle,
  Car,
  Check,
  ClipboardList,
  Clock,
  FileCheck2,
  FileSignature,
  FileText,
  Hourglass,
  RefreshCw,
  Send,
  ShieldCheck,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import {
  mascaraCnpj,
  mascaraCpfCnpj,
  mascaraPlaca,
  mascaraTelefone,
  somenteDigitos,
} from '@/nucleo/formato/documentos';
import type { StatusTone } from '@/nucleo/statusBadgeTokens';
import type { StatusVariant } from '@/ui/StatusBadge';

/* ───────────────────────── Máscaras reaproveitadas ───────────────────────── */

/**
 * As máscaras vivem em `nucleo/formato/documentos.ts` e são reexportadas aqui só
 * para a tela ter um import só. NENHUMA máscara nova nasce neste módulo: uma
 * segunda implementação de CPF divergiria da primeira no dia em que alguém
 * corrigir uma delas.
 */
export { mascaraCpf, mascaraCnpj } from '@/nucleo/formato/documentos';
export { mascaraCpfCnpj, mascaraPlaca, mascaraTelefone, somenteDigitos };

/* ───────────────────────────── Tipo de pessoa ───────────────────────────── */

/**
 * `public.tipo_pessoa` — a NATUREZA LEGAL, e ela tem dois valores só.
 *
 * DECISÃO DO PO, 2026-09-21: cooperativa **não** é um terceiro tipo de pessoa.
 * Ela é pessoa jurídica como qualquer outra, e é o que `nucleo/pessoaNatureza.ts`
 * já dizia (`naturezaDaPessoa` devolve `PJ` para cooperativa). O que distingue a
 * cooperativa é o PERFIL de credenciado, que é outro campo, e o vínculo dela com
 * cada veículo — este sim já existe, por carro, na etapa de frota.
 *
 * Esta lista chegou a ter três valores e foi reduzida: um terceiro tipo de
 * pessoa faria o documento do titular mudar de regra de validação por causa de
 * um perfil comercial, e cooperativa tem CNPJ como qualquer PJ.
 *
 * O campo de perfil (PF / PJ / Cooperativa) entra com o endpoint de inscrição —
 * ver `README.md`, "O que falta".
 */
export const TIPOS_DE_PESSOA = ['PF', 'PJ'] as const;
export type TipoDePessoa = (typeof TIPOS_DE_PESSOA)[number];

export const ROTULO_DO_TIPO_DE_PESSOA: Record<TipoDePessoa, string> = {
  PF: 'Pessoa física',
  PJ: 'Pessoa jurídica',
};

export const OPCOES_DE_TIPO_DE_PESSOA: readonly { value: TipoDePessoa; label: string }[] = [
  { value: 'PF', label: ROTULO_DO_TIPO_DE_PESSOA.PF },
  { value: 'PJ', label: ROTULO_DO_TIPO_DE_PESSOA.PJ },
];

/* ─────────────────────────────── Modalidade ─────────────────────────────── */

/**
 * `public.tipo_veiculo_bancada`. É a modalidade do atendimento — e a chave de
 * precificação depois, no financeiro. `CRECHE` filtra escolas CEI; `ACESSIVEL`
 * exige veículo com ancoragem.
 */
export const MODALIDADES = ['CONVENCIONAL', 'ACESSIVEL', 'ESPECIAL', 'CRECHE'] as const;
export type Modalidade = (typeof MODALIDADES)[number];

/** Os rótulos do ciclo de inscrição (F03) e do edital (D-15). */
export const ROTULO_DA_MODALIDADE: Record<Modalidade, string> = {
  CONVENCIONAL: 'TEG comum',
  ACESSIVEL: 'Acessível',
  ESPECIAL: 'Especial',
  CRECHE: 'Creche',
};

export const EXPLICACAO_DA_MODALIDADE: Record<Modalidade, string> = {
  CONVENCIONAL: 'Atendimento regular de alunos da rede municipal.',
  ACESSIVEL: 'Veículo adaptado, com ancoragem para cadeira de rodas.',
  ESPECIAL: 'Atendimento de alunos da educação especial.',
  CRECHE: 'Atendimento de bebês e crianças pequenas, em CEI.',
};

export const OPCOES_DE_MODALIDADE: readonly { value: Modalidade; label: string }[] =
  MODALIDADES.map((value) => ({ value, label: ROTULO_DA_MODALIDADE[value] }));

/* ──────────────────────── Prioridade da escola ──────────────────────────── */

/**
 * A escola de preferência é RANQUEADA, não uma lista solta: a DTP distribui
 * usando a ordem declarada. Guardar a posição junto do id é o que permite à
 * análise dizer "atendido na 2ª opção" sem adivinhar.
 */
export const PRIORIDADES_DE_ESCOLA = [1, 2, 3] as const;
export type PrioridadeDeEscola = (typeof PRIORIDADES_DE_ESCOLA)[number];

export const ROTULO_DA_PRIORIDADE: Record<PrioridadeDeEscola, string> = {
  1: '1ª opção',
  2: '2ª opção',
  3: '3ª opção',
};

/**
 * Tom do selo de cada prioridade. Token semântico, nunca cor fixa
 * (CLAUDE.md, "Regras de interface", item 2) — a tela passa por
 * `STATUS_TONE[...]` de `nucleo/statusBadgeTokens.ts`.
 */
export const TOM_DA_PRIORIDADE: Record<PrioridadeDeEscola, StatusTone> = {
  1: 'success',
  2: 'info',
  3: 'warning',
};

/* ──────────────────────── Status da inscrição ───────────────────────────── */

/**
 * Os onze valores de `public.status_inscricao`, na ordem do tipo enumerado.
 *
 * A lista é `as const` de propósito: `contrato.spec.ts` cruza `Object.keys` da
 * apresentação com ela, e status novo sem rótulo derruba o teste em vez de
 * chegar à tela como `PENDENTE_CONSENTIMENTO` em caixa alta.
 */
export const STATUS_DA_INSCRICAO = [
  'RASCUNHO',
  'ENVIADO',
  'PENDENTE_CONSENTIMENTO',
  'EM_ANALISE',
  'PRE_CREDENCIADO',
  'REPROVADO',
  'DESISTENTE',
  'PENDENTE_DOCUMENTOS',
  'AGUARDANDO_CORRECAO',
  'REENVIADO',
  'EXPIRADO',
] as const;

export type StatusDaInscricao = (typeof STATUS_DA_INSCRICAO)[number];

/** O que a tela precisa saber para desenhar um status: texto, selo e superfície. */
export interface ApresentacaoDoStatus {
  /** Rótulo em português, como o inscrito lê. */
  rotulo: string;
  /** Uma frase dizendo o que está acontecendo — e, quando cabe, o que fazer. */
  descricao: string;
  /** Ícone do Lucide (CLAUDE.md, "Regras de interface", item 6). */
  icone: LucideIcon;
  /** Variante para `<StatusBadge status=… />`. */
  variante: StatusVariant;
  /** Tom correspondente, para a superfície do cartão (`STATUS_TONE[tom]`). */
  tom: StatusTone;
}

/**
 * A apresentação de cada status.
 *
 * DE ONDE VEM CADA ESCOLHA. Rótulo e descrição são os do protótipo
 * (`statusConfig`, uma linha por status). A VARIANTE segue duas regras, nesta
 * ordem: existe variante homônima em `StatusBadge`? usa-se ela (`rascunho`,
 * `expirado`); não existe? usa-se a do mapa de status do protótipo
 * (`INSCRICAO_STATUS_MAP`). O `tom` é sempre o que `StatusBadge` renderiza para
 * aquela variante — selo e superfície não podem discordar de cor na mesma tela.
 *
 * O protótipo tem DOIS mapas que discordam entre si (`INSCRICAO_STATUS_MAP` diz
 * `rejeitado` para `EXPIRADO`; `statusConfig` diz destrutivo; a tela de consulta
 * diz neutro). Vale a homônima: expirar não é ser reprovado, e pintar de
 * vermelho um prazo vencido faria o inscrito ler recusa onde houve silêncio.
 */
export const APRESENTACAO_DO_STATUS: Record<StatusDaInscricao, ApresentacaoDoStatus> = {
  RASCUNHO: {
    rotulo: 'Rascunho',
    descricao: 'Sua inscrição ainda está em preenchimento.',
    icone: FileText,
    variante: 'rascunho',
    tom: 'neutral',
  },
  ENVIADO: {
    rotulo: 'Enviada',
    descricao: 'Recebemos sua inscrição. Em breve ela entrará em análise.',
    icone: Send,
    variante: 'pendente',
    tom: 'warning',
  },
  PENDENTE_CONSENTIMENTO: {
    rotulo: 'Pendente de consentimento',
    descricao: 'Aguardando a confirmação do titular do veículo para validar o vínculo.',
    icone: ShieldCheck,
    variante: 'pendente',
    tom: 'warning',
  },
  EM_ANALISE: {
    rotulo: 'Em análise',
    descricao: 'A equipe da DTP está analisando sua inscrição.',
    icone: Hourglass,
    variante: 'em_execucao',
    tom: 'warning',
  },
  PRE_CREDENCIADO: {
    rotulo: 'Pré-credenciada',
    descricao: 'Aprovada — aguardando a assinatura do termo de adesão.',
    icone: FileCheck2,
    variante: 'ativo',
    tom: 'success',
  },
  REPROVADO: {
    rotulo: 'Reprovada',
    descricao: 'Inscrição reprovada. Veja o parecer para entender o motivo.',
    icone: XCircle,
    variante: 'rejeitado',
    tom: 'destructive',
  },
  DESISTENTE: {
    rotulo: 'Desistente',
    descricao: 'Desistência registrada. Você pode iniciar uma nova inscrição.',
    icone: XCircle,
    variante: 'cancelado',
    tom: 'destructive',
  },
  PENDENTE_DOCUMENTOS: {
    rotulo: 'Pendente de documentos',
    descricao: 'Há documentos a enviar. Confira a lista de pendências.',
    icone: AlertCircle,
    variante: 'pendente',
    tom: 'warning',
  },
  AGUARDANDO_CORRECAO: {
    rotulo: 'Aguardando correção',
    descricao: 'Há dados a corrigir. Confira a mensagem da equipe da DTP.',
    icone: AlertCircle,
    variante: 'pendente',
    tom: 'warning',
  },
  REENVIADO: {
    rotulo: 'Reenviada para análise',
    descricao: 'Reenvio recebido. Aguarde a nova análise.',
    icone: RefreshCw,
    variante: 'em_execucao',
    tom: 'warning',
  },
  EXPIRADO: {
    rotulo: 'Expirada',
    descricao: 'O prazo de correção terminou sem reenvio.',
    icone: Clock,
    variante: 'expirado',
    tom: 'neutral',
  },
};

/** Rótulo do status, com o valor cru como último recurso — nunca tela em branco. */
export function rotuloDoStatus(status: string | null | undefined): string {
  if (!status) {
    return '—';
  }
  return APRESENTACAO_DO_STATUS[status as StatusDaInscricao]?.rotulo ?? status;
}

/** A apresentação de um status, ou a de `EM_ANALISE` se vier valor desconhecido. */
export function apresentacaoDoStatus(status: string | null | undefined): ApresentacaoDoStatus {
  return APRESENTACAO_DO_STATUS[status as StatusDaInscricao] ?? APRESENTACAO_DO_STATUS.EM_ANALISE;
}

/* ──────────────────── Os três passos do acompanhamento ──────────────────── */

/** Um passo da linha do tempo pública: Envio → Análise → Decisão. */
export interface PassoDoAcompanhamento {
  chave: 'envio' | 'analise' | 'decisao';
  rotulo: string;
  /** Os status que colocam a inscrição neste passo. */
  status: readonly StatusDaInscricao[];
}

/**
 * Os onze status viram TRÊS passos na tela do inscrito.
 *
 * Quem se inscreveu não precisa distinguir `REENVIADO` de `PENDENTE_DOCUMENTOS`
 * para saber onde está: os dois são "em análise". A granularidade fina fica no
 * selo de status; a linha do tempo responde só "falta muito?".
 */
export const PASSOS_DO_ACOMPANHAMENTO: readonly PassoDoAcompanhamento[] = [
  { chave: 'envio', rotulo: 'Envio', status: ['RASCUNHO', 'ENVIADO', 'PENDENTE_CONSENTIMENTO'] },
  {
    chave: 'analise',
    rotulo: 'Análise',
    status: ['EM_ANALISE', 'PENDENTE_DOCUMENTOS', 'AGUARDANDO_CORRECAO', 'REENVIADO'],
  },
  {
    chave: 'decisao',
    rotulo: 'Decisão',
    status: ['PRE_CREDENCIADO', 'REPROVADO', 'EXPIRADO', 'DESISTENTE'],
  },
];

/** Índice do passo atual, ou `0` quando o status não for reconhecido. */
export function indiceDoPasso(status: StatusDaInscricao | string | null | undefined): number {
  const indice = PASSOS_DO_ACOMPANHAMENTO.findIndex((passo) =>
    passo.status.includes(status as StatusDaInscricao),
  );
  return indice < 0 ? 0 : indice;
}

/** Status de decisão positiva: a linha do tempo fecha inteira. */
export function ehDesfechoPositivo(status: StatusDaInscricao | string | null | undefined): boolean {
  return status === 'PRE_CREDENCIADO';
}

/** Status que admitem reenvio — é o que decide se o bloco de correção aparece. */
export function admiteReenvio(status: StatusDaInscricao | string | null | undefined): boolean {
  return status === 'PENDENTE_DOCUMENTOS' || status === 'AGUARDANDO_CORRECAO';
}

/** Status encerrados, em que só cabe começar de novo. */
export function ehStatusEncerrado(status: StatusDaInscricao | string | null | undefined): boolean {
  return status === 'EXPIRADO' || status === 'DESISTENTE' || status === 'REPROVADO';
}

/* ─────────────────── As quatro etapas de cada assistente ────────────────── */

/** Uma etapa de assistente: número, rótulo, o que se faz nela e o ícone. */
export interface EtapaDoAssistente {
  numero: 1 | 2 | 3 | 4;
  rotulo: string;
  descricao: string;
  icone: LucideIcon;
}

/**
 * Autocadastro — a inscrição de uma entidade do SGTP no período aberto (F05).
 * As quatro etapas do protótipo, com a primeira trocada: a frota vem do SGTP
 * veículo a veículo (não por licença), junto com os contatos da inscrição.
 */
export const ETAPAS_DO_AUTOCADASTRO: readonly EtapaDoAssistente[] = [
  {
    numero: 1,
    rotulo: 'Veículos SGTP',
    descricao: 'Contatos e veículos da entidade',
    icone: ClipboardList,
  },
  {
    numero: 2,
    rotulo: 'Configurar veículos',
    descricao: 'Modalidade, DRE, escolas e cooperativa por carro',
    icone: Car,
  },
  {
    numero: 3,
    rotulo: 'Aceite do Edital',
    descricao: 'Edital e privacidade',
    icone: FileSignature,
  },
  { numero: 4, rotulo: 'Confirmação', descricao: 'Revisão e envio', icone: Check },
];

/** Estimativa de tempo mostrada antes de começar, para calibrar expectativa. */
export const TEMPO_ESTIMADO_DO_AUTOCADASTRO = '≈ 8 min';

/* ────────────────────────── Tipos de documento ──────────────────────────── */

/** Os anexos que a inscrição aceita, como no protótipo (`tiposDocumento`). */
export const TIPOS_DE_DOCUMENTO = [
  'RG_CNH',
  'COMPROVANTE_ENDERECO',
  'CRLV',
  'APOLICE_SEGURO',
  'CERTIDAO_CRIMINAL',
  'OUTRO',
] as const;

export type TipoDeDocumento = (typeof TIPOS_DE_DOCUMENTO)[number];

export const ROTULO_DO_TIPO_DE_DOCUMENTO: Record<TipoDeDocumento, string> = {
  RG_CNH: 'RG ou CNH',
  COMPROVANTE_ENDERECO: 'Comprovante de endereço',
  CRLV: 'CRLV do veículo',
  APOLICE_SEGURO: 'Apólice de seguro',
  CERTIDAO_CRIMINAL: 'Certidão criminal',
  OUTRO: 'Outro documento',
};

/** Rótulo do tipo, com o valor cru como último recurso. */
export function rotuloDoTipoDeDocumento(tipo: string | null | undefined): string {
  if (!tipo) {
    return '—';
  }
  return ROTULO_DO_TIPO_DE_DOCUMENTO[tipo as TipoDeDocumento] ?? tipo;
}

/* ───────────────────── Apoio ao preenchimento das telas ─────────────────── */

/** Diretoria regional de ensino, no recorte que o portal precisa. */
export interface Dre {
  id: string;
  codigo: string;
  nome: string;
}

/** Uma escola escolhida, com a posição que ela ocupa na preferência. */
export interface EscolaDePreferencia {
  escola_id: string;
  nome: string;
  dre_id: string | null;
  prioridade: PrioridadeDeEscola;
}

/**
 * O veículo como o inscrito o declara — vai para `capacidade_declarada` (jsonb).
 * `origem_sgtp` distingue o que veio da integração do que foi digitado à mão.
 */
export interface VeiculoDeclarado {
  numero_placa: string;
  modelo: string;
  ano: string;
  modalidade: Modalidade;
  numero_licenca: string | null;
  capacidade_total: number | null;
  origem_sgtp: boolean;
}

/* ──────────────────────────── Inscrição e anexos ────────────────────────── */

/** `public.inscricao_pendencias` — o que a DTP pediu e ainda não foi resolvido. */
export interface PendenciaDaInscricao {
  id: string;
  descricao: string;
  tipo: string;
  anexo_tipo: TipoDeDocumento | null;
  /** ISO com fuso. */
  prazo_em: string | null;
  resolvida_em: string | null;
  criada_em: string;
}

/** `public.inscricao_transicoes` — a linha do tempo, como o inscrito a lê. */
export interface TransicaoDaInscricao {
  status_de: StatusDaInscricao | null;
  status_para: StatusDaInscricao;
  /** Quem moveu: `DTP`, `USUARIO` ou `SISTEMA`. */
  autor_tipo: string;
  motivo: string | null;
  mensagem_publica: string | null;
  criado_em: string;
}

/** Mensagem trocada entre a DTP e o inscrito. */
export interface MensagemDaInscricao {
  id: string;
  autor_tipo: string;
  conteudo: string;
  criada_em: string;
  lida_em: string | null;
}

/** `public.documentos_inscricao`, no recorte que o portal mostra. */
export interface DocumentoDaInscricao {
  id: string;
  tipo: TipoDeDocumento | string;
  nome_original: string;
  versao: number;
  criado_em: string;
}

/**
 * A inscrição inteira, como a tela de acompanhamento a recebe.
 *
 * `escolas_preferencia` e `capacidade_declarada` são `jsonb` no banco; aqui já
 * chegam tipados, porque quem lê a tela não tem como validar JSON solto.
 */
export interface Inscricao {
  id: string;
  protocolo: string;
  tipo_pessoa: TipoDePessoa;
  nome_razao_social: string;
  cpf_cnpj: string;
  email: string | null;
  telefone: string | null;
  dre_id: string | null;
  dre_nome: string | null;
  modalidade: Modalidade | null;
  numero_licenca: string | null;
  licenca_sgtp: string | null;
  numero_placa: string | null;
  capacidade_declarada: VeiculoDeclarado[];
  escolas_preferencia: EscolaDePreferencia[];
  aceite_edital_em: string | null;
  edital_versao: string | null;
  data_envio: string | null;
  data_analise: string | null;
  status_inscricao: StatusDaInscricao;
  parecer: string | null;
  prazo_correcao_em: string | null;
  expira_em: string | null;
  reenvios_count: number;
  ultimo_motivo_devolucao: string | null;
  created_at: string;
  updated_at: string;
  documentos: DocumentoDaInscricao[];
  pendencias: PendenciaDaInscricao[];
  transicoes: TransicaoDaInscricao[];
  mensagens: MensagemDaInscricao[];
}

/**
 * A inscrição no resumo da listagem "minhas inscrições".
 *
 * Um CPF pode ter várias: o protótipo cria UMA INSCRIÇÃO POR VEÍCULO, com
 * protocolo próprio. Por isso a tela lista, em vez de abrir direto — a não ser
 * quando só existe uma.
 */
export interface InscricaoResumo {
  protocolo: string;
  status_inscricao: StatusDaInscricao;
  tipo_pessoa: TipoDePessoa | null;
  nome_razao_social: string | null;
  modalidade: Modalidade | null;
  numero_placa: string | null;
  numero_licenca: string | null;
  escola_principal: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/* ───────────────────────────── Formatadores ─────────────────────────────── */

/** `21 set 2026, 14:30`. Sem valor vira travessão, nunca `Invalid Date`. */
export function formatarDataHora(valor: string | null | undefined): string {
  if (!valor) {
    return '—';
  }
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) {
    return '—';
  }
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** `21/09/2026`. */
export function formatarData(valor: string | null | undefined): string {
  if (!valor) {
    return '—';
  }
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) {
    return '—';
  }
  return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Quantos dias faltam até a data — negativo quando já passou.
 *
 * A tela usa para dizer "prazo hoje" e "urgente": o inscrito precisa do número,
 * não da diferença em milissegundos.
 */
export function diasAte(valor: string | null | undefined): number | null {
  if (!valor) {
    return null;
  }
  const alvo = new Date(valor);
  if (Number.isNaN(alvo.getTime())) {
    return null;
  }
  return Math.ceil((alvo.getTime() - Date.now()) / 86_400_000);
}

/** Prazo apertado: até dois dias. Mesma régua do protótipo. */
export function prazoEhUrgente(valor: string | null | undefined): boolean {
  const dias = diasAte(valor);
  return dias !== null && dias <= 2;
}

/**
 * CPF com só os dois últimos dígitos à mostra: `•••.•••.•••-09`.
 *
 * CNPJ sai por inteiro — é registro público da empresa, não dado pessoal
 * (CLAUDE.md, "Regras de API", item 5, e `docs/LGPD-E-ACESSIBILIDADE.md`).
 */
export function documentoParcialmenteOculto(valor: string | null | undefined): string {
  if (!valor) {
    return '—';
  }
  const digitos = somenteDigitos(valor);
  if (digitos.length !== 11) {
    return mascaraCpfCnpj(digitos);
  }
  return `•••.•••.•••-${digitos.slice(9)}`;
}

/** Documento por extenso, para o próprio dono ver o que digitou. */
export function formatarDocumento(valor: string | null | undefined): string {
  const formatado = mascaraCpfCnpj(valor);
  return formatado || '—';
}

/** Placa com o traço do padrão antigo, sem traço no Mercosul. */
export function formatarPlaca(valor: string | null | undefined): string {
  const formatada = mascaraPlaca(valor);
  return formatada || '—';
}

/**
 * A mensagem de pendência do servidor cita a placa como está gravada
 * (`EXE1234`); na tela, ela aparece como em todo o resto da jornada
 * (`EXE-1234`). Só a exibição muda — a placa gravada e a do contrato ficam como
 * vieram.
 */
export function comPlacaFormatada(mensagem: string, placa: string | null | undefined): string {
  if (!placa) return mensagem;
  return mensagem.split(placa).join(formatarPlaca(placa));
}

/** Telefone com DDD entre parênteses. */
export function formatarTelefone(valor: string | null | undefined): string {
  const formatado = mascaraTelefone(valor);
  return formatado || '—';
}

/**
 * CNPJ com a pontuação: `90000001000129` → `90.000.001/0001-29`.
 *
 * O documento da entidade chega de dois jeitos: CNPJ cru (PJ, registro público)
 * e CPF já mascarado pela API (PF, `•••.•••.•••-56`). Só o primeiro ganha
 * máscara; qualquer outra coisa sai como veio — mascarar o CPF oculto de novo
 * apagaria os pontos e deixaria só os dois dígitos.
 */
export function formatarCnpj(valor: string | null | undefined): string {
  if (!valor) {
    return '—';
  }
  return somenteDigitos(valor).length === 14 && /^[\d./-]+$/.test(valor.trim())
    ? mascaraCnpj(valor)
    : valor;
}

/**
 * Nome da DRE com o prefixo "DRE" uma vez só.
 *
 * O cadastro guarda o nome ora com o prefixo (`DRE Butantã`), ora sem
 * (`Butantã`); prefixar às cegas produz "DRE DRE Butantã".
 */
export function rotuloDaDre(nome: string | null | undefined): string {
  const limpo = nome?.trim();
  if (!limpo) {
    return '—';
  }
  return /^DRE(\s|$)/i.test(limpo) ? limpo : `DRE ${limpo}`;
}

/** Rótulo de modalidade, com o valor cru como último recurso. */
export function rotuloDaModalidade(valor: string | null | undefined): string {
  if (!valor) {
    return '—';
  }
  return ROTULO_DA_MODALIDADE[valor as Modalidade] ?? valor;
}

/** Rótulo do tipo de pessoa, com o valor cru como último recurso. */
export function rotuloDoTipoDePessoa(valor: string | null | undefined): string {
  if (!valor) {
    return '—';
  }
  return ROTULO_DO_TIPO_DE_PESSOA[valor as TipoDePessoa] ?? valor;
}

/**
 * As escolas em ordem de preferência.
 *
 * Ordenar na leitura, e não confiar na ordem do `jsonb`: o banco não promete
 * ordem de array, e a 1ª opção aparecendo em segundo lugar na tela é erro que
 * ninguém percebe até alguém reclamar da distribuição.
 */
export function escolasEmOrdemDePreferencia(
  escolas: readonly EscolaDePreferencia[] | null | undefined,
): EscolaDePreferencia[] {
  return [...(escolas ?? [])].sort((a, b) => a.prioridade - b.prioridade);
}

/** Paginação do servidor, no mesmo formato do resto do sistema. */
export interface Pagina<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanho: number;
}
